import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import FolderIcon from '@mui/icons-material/Folder';

import Modal from '../../modules/Modal';
import { ContainedButton } from '../../modules/Buttons';
import { FOLDER_TYPE } from '../../../enums';
import { getListArchivedFolders, restoreArchivedFolder } from '../../../api';
import { findSearchValue } from '../../../helpers';
import styles from '../DeletedVideofeedsModal/DeletedVideofeedsModal.module.scss';

type Props = {
  open: boolean;
  switchModal: () => void;
  onRestored?: () => void | Promise<void>;
};

const formatDate = (iso?: string | Date) => {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} · ${String(
    d.getHours(),
  ).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const DeletedFoldersModal: React.FC<Props> = ({ open, switchModal, onRestored }) => {
  const [folders, setFolders] = useState<FOLDER_TYPE[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const notification = useSnackbar();
  const folderId = findSearchValue('folderId');

  const fetchFolders = async () => {
    try {
      setLoading(true);
      const res = await getListArchivedFolders(folderId);
      setFolders(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) void fetchFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, folderId]);

  const onRestore = async (id: number) => {
    try {
      setRestoringId(id);
      await restoreArchivedFolder(id);
      notification.showMessage('Folder restored');
      if (onRestored) await Promise.resolve(onRestored());
      switchModal();
    } catch (err: any) {
      console.error(err);
      notification.showMessage(err?.response?.data?.message || 'Failed to restore folder');
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Modal open={open} onClose={restoringId ? undefined : switchModal} loading={loading}>
      <div className={styles.modalBody}>
        <div className={styles.headerBlock}>
          <div className={styles.modalEyebrow}>Archive</div>
          <div className={styles.modalTitle}>
            Deleted folders
            {!!folders.length && <span className={styles.count}>{folders.length}</span>}
          </div>
          <div className={styles.modalSubtitle}>
            Restore a folder with its subfolders and videofeeds. Deleted folders can be restored for 30 days after deletion.
          </div>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.titleColumn}>Name</th>
                <th className={styles.dateColumn}>Deleted at</th>
                <th className={styles.idColumn}>ID</th>
                <th className={styles.actionsColumn}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && folders.length === 0 ? (
                <tr>
                  <td colSpan={4} className={styles.emptyCell}>
                    No deleted folders here
                  </td>
                </tr>
              ) : (
                folders.map((folder) => (
                  <tr key={folder.id}>
                    <td className={styles.titleCell}>
                      <div className={styles.titleContent}>
                        <FolderIcon className={styles.typeIcon} />
                        <span className={styles.titleText}>{folder.name}</span>
                      </div>
                    </td>
                    <td>{formatDate((folder as any).deleted_at || (folder as any).updated_at)}</td>
                    <td>{folder.id}</td>
                    <td>
                      <ContainedButton
                        kind="purple"
                        disabled={restoringId === Number(folder.id)}
                        onClick={() => onRestore(Number(folder.id))}
                      >
                        {restoringId === Number(folder.id) ? 'Restoring...' : 'Restore'}
                      </ContainedButton>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
};

export default DeletedFoldersModal;
